<template>
  <DSDialog
    :open="isOpen"
    @update:open="handleUpdateOpen"
  >
    <DSDialogContent class="max-w-ds-dialog-lg gap-0 p-0">
      <DSDialogHeader class="border-b border-border">
        <div class="flex items-center gap-2">
          <DSButton
            v-if="onBack"
            variant="ghost"
            size="icon-sm"
            class="-ml-2 shrink-0"
            aria-label="Back to add asset"
            @click="handleBack"
          >
            <ArrowLeft class="size-4" />
          </DSButton>
          <DSDialogTitle class="text-xl">
            Add asset
          </DSDialogTitle>
        </div>
        <DSDialogDescription class="sr-only">
          Review what Allposit read from your message, fill in what it could not,
          then create the records.
        </DSDialogDescription>
      </DSDialogHeader>

      <!--
        The create already happened by the time this shows — see handleCreate.
        Checked first of all: `flow` and `answer` are both still set once the
        records exist, and a wizard or a rejection screen drawn under its own
        success screen would be a form the user could keep editing after what
        it described is already real.
      -->
      <WizardCreated
        v-if="created"
        :records="createdRows"
        :net-equity="createdNetEquity"
        :summary="createdSummary"
        :new-account-name="createdNewAccountName"
        :new-account-asset="createdNewAccountAsset"
        :tags="createdValues?.tags"
        :currency="createdValues?.currency"
        @open="handleOpenAsset"
        @add-another="handleBack"
        @go-to-balance-sheet="handleGoToBalanceSheet"
      />

      <!--
        Three ways this ends without a wizard: the assistant is still reading,
        it failed, or it read the sentence as nothing any wizard covers. None of
        them may show a half-filled wizard — a flow the user did not ask for is
        worse than no flow at all.
      -->
      <WizardReading
        v-else-if="loading"
        :sentence
      />

      <WizardUnread
        v-else-if="!flow && (errorMessage || !answer)"
        :sentence
        :message="errorMessage ?? DEFAULT_UNREAD_MESSAGE"
        @edit-sentence="handleBack"
        @retry="assetWizardStore.retry"
        @pick-flow="handleUpdateFlowWithReset"
      />

      <WizardUnread
        v-else-if="!flow && answer && !isAssetWizardAnswer(answer)"
        :sentence
        :message="answer.message"
        :questions="answer.questions"
        :examples="answer.examples"
        @edit-sentence="handleBack"
        @example="assetWizardStore.submit"
        @pick-flow="handleUpdateFlowWithReset"
      />

      <!--
        Keyed on the flow: switching it is a different schema and a different set
        of steps, so the wizard is rebuilt rather than reconfigured.
      -->
      <Wizard
        v-else-if="flow"
        :key="flow"
        :definition="WIZARD_BY_FLOW[flow]"
        :answer="recognized"
        :sentence
        :creating
        @update:flow="handleUpdateFlow"
        @edit-sentence="handleBack"
        @create="handleCreate"
      />
    </DSDialogContent>
  </DSDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowLeft } from '@lucide/vue'

import { ModalClass } from '@types'
import { ROUTE_NAME } from '@/const'
import {
  AssetWizardFlow,
  isAssetWizardAnswer,
} from '@/store/ai/assetWizard/utils/types'

import { useAIAssetWizardStore } from '@/store/ai/assetWizard'
import { useAIAccountSuggestionStore } from '@/store/ai/accountSuggestion'
import { useAIAccountTypeSuggestionStore } from '@/store/ai/accountTypeSuggestion'
import { useAIMortgageAccountSuggestionStore } from '@/store/ai/mortgageAccountSuggestion'
import { useAITagSuggestionStore } from '@/store/ai/tagSuggestion'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { AssetClass } from '@/entities/assets'

import { handleCatchedError } from '@/helpers/common'

import {
  DSButton,
  DSDialog,
  DSDialogContent,
  DSDialogDescription,
  DSDialogHeader,
  DSDialogTitle,
  DSToast,
} from '@ds'

import Wizard from './Wizard.vue'
import WizardCreated from './components/WizardCreated.vue'
import WizardReading from './components/WizardReading.vue'
import WizardUnread from './components/WizardUnread.vue'

import {
  createdRecordRows,
  createdSummaryText,
  netEquityOf,
  newlyCreatedAccountName,
} from './utils/helpers'
import { WIZARD_BY_FLOW } from './utils/registry'
import { WizardValues } from './utils/types'

/*
 * The modal-facing half of the wizards: it decides *which* wizard, and Wizard
 * itself runs it.
 *
 * Two ways in. With `flow`, it opens that wizard at step one — a direct entry,
 * nothing read, nothing prefilled. Without one it waits on the assistant, which
 * was handed the sentence before this dialog opened (see
 * AddAssetDialog.handleDescribe), and opens the recognized wizard at its
 * summary. The answer is read off the store rather than passed in, so a retry
 * from in here lands in the same place as the first read.
 */
type Props = {
  modal: ModalClass<any>
  /** Opens this wizard directly, at its first step. Omit to wait on the assistant. */
  flow?: AssetWizardFlow
  /** Returns to the Add asset dialog with the sentence intact. */
  onBack?: () => void
  /** Ends the whole flow — the Add asset dialog behind this one is hidden, not closed. */
  onDismiss?: () => void
}

const props = defineProps<Props>()

const router = useRouter()

const assetWizardStore = useAIAssetWizardStore()
const accountSuggestionStore = useAIAccountSuggestionStore()
const accountTypeSuggestionStore = useAIAccountTypeSuggestionStore()
const mortgageAccountSuggestionStore = useAIMortgageAccountSuggestionStore()
const tagSuggestionStore = useAITagSuggestionStore()
const assetsBunchStore = useAssetsBunchStore()

const DEFAULT_UNREAD_MESSAGE =
  'The assistant could not read that. Edit the sentence and try again.'

const isOpen = computed(() => props.modal.isOpened)

const loading = computed(() => !props.flow && assetWizardStore.isLoading)
const errorMessage = computed(() => assetWizardStore.getErrorMessage)
/*
 * A direct entry has nothing to do with the assistant, and the store is not
 * emptied when the wizard closes — so a sentence read half an hour ago would
 * otherwise still be here, prefilling fields the user never described and
 * pointing three suggestion assistants at a question about a different asset.
 * With `flow` given, this dialog is deliberately blind to the store.
 */
const isAssisted = computed(() => !props.flow)

const answer = computed(() => (isAssisted.value ? assetWizardStore.getAnswer : undefined))
const sentence = computed(() => (isAssisted.value ? assetWizardStore.getSentence : ''))

/** The answer only when it named a flow. Anything else is handled above. */
const recognized = computed(() => {
  const value = answer.value
  return value && isAssetWizardAnswer(value) ? value : undefined
})

/*
 * Which wizard is on screen. Seeded from the prop for a direct entry and from
 * the assistant otherwise, then owned here — the flow picker writes to it, and
 * the assistant's own reading must not overwrite a flow the user just corrected.
 */
const flow = ref<AssetWizardFlow | undefined>(props.flow)

/*
 * Every "Ask AI" answer is scoped to one flow's own account list, account
 * types and tag vocabulary — a suggestion asked for while on one flow is not
 * an answer to the question a different flow asks. `immediate` covers a
 * wizard that inherited a suggestion from whichever one the user had open
 * before this one (the stores are app-wide singletons, not this dialog's own
 * state), and the watch itself covers a flow corrected in place — the
 * dropdown on the wizard's own header, or a fresh pick from WizardUnread.
 */
watch(flow, value => {
  if (!value) return
  accountSuggestionStore.reset()
  accountTypeSuggestionStore.reset()
  mortgageAccountSuggestionStore.reset()
  tagSuggestionStore.reset()
}, { immediate: true })

/** The create is running. Held here because this is what owns the modal. */
const creating = ref(false)

/**
 * What the create actually wrote, once it has. Its presence is what switches
 * the dialog to WizardCreated — see the template — so this doubles as "has the
 * create already happened" for a wizard that is otherwise still on screen.
 */
const created = ref<AssetClass[]>()

/**
 * The values the create actually ran with — held onto for WizardCreated,
 * which reads the account, the currency and each record's own amount back
 * off them. `ensureOwnerAccount` (see utils/create.ts) writes the resolved
 * account id onto this same object while `create()` runs, so by the time it
 * resolves this already names the account a new one landed in, not only one
 * the user picked.
 */
const createdValues = ref<WizardValues>()

const createdDefinition = computed(() => (flow.value ? WIZARD_BY_FLOW[flow.value] : undefined))

const createdRows = computed(() => {
  if (!created.value || !createdDefinition.value || !createdValues.value) return []
  return createdRecordRows(createdDefinition.value, created.value, createdValues.value)
})

const createdNetEquity = computed(() => netEquityOf(createdRows.value))

const createdSummary = computed(() =>
  createdValues.value ? createdSummaryText(createdRows.value, createdValues.value) : '',
)

const createdNewAccountName = computed(() =>
  createdValues.value ? newlyCreatedAccountName(createdValues.value) : undefined,
)

/** Best-effort: absent leaves WizardCreated's "Open account" link out rather than broken. */
const createdNewAccountAsset = computed(() => {
  const id = createdValues.value?.ownerAccountId as string | undefined
  if (!createdNewAccountName.value || !id) return undefined
  return assetsBunchStore.getElementById(id)
})

watch(recognized, value => {
  if (!value) return
  flow.value = value.flow
}, { immediate: true })

const handleUpdateOpen = (value: boolean) => {
  if (value) return

  props.modal.close()
  props.onDismiss?.()
}

const handleBack = () => {
  props.modal.close()
  props.onBack?.()
}

const handleUpdateFlow = (value: AssetWizardFlow) => {
  flow.value = value
}

const handleUpdateFlowWithReset = (value: AssetWizardFlow) => {
  handleUpdateFlow(value)
  assetWizardStore.reset()
}

/**
 * Writes the records, then shows the user what it made.
 *
 * The steps are the flow's own — see its `create.ts` — and this is only what
 * surrounds them: the wait, where a failure is said, and what happens after.
 *
 * A failure leaves whatever already landed. There is no transaction spanning
 * these calls and a compensating delete can fail in its own right, so the
 * honest thing is to report it and keep the dialog: the form is intact, the
 * user can see what was asked for, and nothing has been claimed that is not
 * true. `handleCatchedError` is what puts it on screen and in Faro.
 */
const handleCreate = async (values: WizardValues) => {
  const definition = flow.value && WIZARD_BY_FLOW[flow.value]
  if (!definition || creating.value) return

  creating.value = true
  try {
    /*
     * The dialog stays open on WizardCreated rather than closing straight to a
     * drawer: a financed property also creates a mortgage, and closing on the
     * first record left the second one to find on its own in the accounts
     * list. Every record it wrote gets its own row there, with its own way in.
     */
    created.value = await definition.create(values)
    createdValues.value = values
  } catch (e) {
    DSToast.error((e as Error).message, {
      position: 'top-right',
      duration: 5000,
    })
    handleCatchedError(e as string)
  } finally {
    creating.value = false
  }
}

/** WizardCreated's "View" / "Open account" — ends the wizard on the record the user asked for. */
const handleOpenAsset = (asset: AssetClass) => {
  props.modal.close()
  props.onDismiss?.()
  asset.openDrawer()
}

/** WizardCreated's "Go to balance sheet" — ends the wizard there. */
const handleGoToBalanceSheet = () => {
  props.modal.close()
  props.onDismiss?.()
  router.push({ name: ROUTE_NAME.ASSETS })
}
</script>
